import { Formik } from 'formik';
import { isEmpty } from 'lodash';
import React, { useState, useEffect } from 'react';
import Form, { FormElement, Label } from '@dashboard/components/Form';
import FormSubmit from '@dashboard/components/Formik/components/FormSubmit';
import { TextInput } from '@dashboard/components/Formik';
import FormError, { FormErrorBase } from '@dashboard/components/Formik/components/FormError';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import useUserQueries from '@dashboard/hooks/queries/useUserQueries';
import { transformApiErrorToFormik } from '@dashboard/services/form';
import { useMutation } from '@tanstack/react-query';
import { fetcher } from '@dashboard/services/request';
import { toast } from '@dashboard/context/ToastContext';
import { Button } from '@dashboard/components/ui/button';
import { Alert, AlertDescription } from '@dashboard/components/ui/alert';

const EmailForm = () => {
  const { user, setUser, token } = useAuth();
  const [pendingEmailChange, setPendingEmailChange] = useState(user.pendingEmail);
  const { updateUser } = useUserQueries();

  // Update pendingEmailChange whenever user changes
  useEffect(() => {
    if (user && user.pendingEmail) {
      setPendingEmailChange(user.pendingEmail);
    }
  }, [user]);

  const resendActivationEmailMutation = useMutation({
    mutationFn: () =>
      fetcher(`/users/${user._id}/resend-activation-email`, token, {
        method: 'POST',
      }),
    onSuccess: () => {
      toast({
        title: 'Activation email sent',
        type: 'success',
      });
    },
    onError: () => {
      toast({
        title: 'Unable to resend activation email',
        description: 'Please try again or contact support',
        type: 'error',
      });
    },
  });

  return (
    <Formik
      enableReinitialize
      initialValues={{
        email: user.email,
      }}
      onSubmit={async (values, actions) => {
        // Only proceed if email has changed
        if (values.email === user.email) {
          actions.setStatus('Email is unchanged');
          return;
        }

        actions.setStatus(undefined);

        const { data, error } = await updateUser(values, user._id);

        if (data) {
          actions.setStatus('');

          // If we have a message in the response, it means the email change requires verification
          if (data.message) {
            setPendingEmailChange(values.email);
            actions.resetForm({
              values: {
                email: user.email, // Keep the form showing the current email
              },
            });

            toast({
              title: 'Verification email sent',
              description: data.message,
              type: 'success',
            });
          } else {
            actions.resetForm({
              values: {
                email: data.email,
              },
            });
            setUser(data);
          }
        } else {
          const { status, errors } = transformApiErrorToFormik(error);

          if (!isEmpty(errors)) {
            actions.setErrors(errors);
          } else {
            actions.setStatus(status);
          }
        }
      }}
    >
      {({ status, dirty, handleSubmit }) => (
        <Form onSubmit={handleSubmit}>
          {pendingEmailChange && (
            <Alert className="mb-4">
              <AlertDescription>
                <p>
                  You have a pending email change to: <strong>{pendingEmailChange}</strong>
                </p>
                <p>Check your email and click the verification link to complete the change.</p>
              </AlertDescription>
            </Alert>
          )}

          <FormElement>
            <Label htmlFor="email">Email</Label>
            <TextInput type="email" id="email" name="email" placeholder="Email" required />
            <FormError name="email" />
            {status && <FormErrorBase>{status}</FormErrorBase>}

            {!user.active && (
              <div className="mt-2 text-xs">
                Your email is not verified. Please verify your email to continue.{' '}
                <Button
                  onClick={() => resendActivationEmailMutation.mutate()}
                  variant="link"
                  size="sm"
                  className="text-xs h-auto p-0"
                  disabled={resendActivationEmailMutation.isPending}
                >
                  Resend activation email
                </Button>
              </div>
            )}
          </FormElement>

          <FormSubmit>
            <FormSubmit.Button>Save</FormSubmit.Button>
            <FormSubmit.State visible={!!dirty} />
          </FormSubmit>
        </Form>
      )}
    </Formik>
  );
};

export default EmailForm;
